import { useState, useEffect, useRef, useMemo, useCallback, type CSSProperties } from 'react'
import { Plus, Link2, Settings, Check, Search, Cable, Pencil, LogOut } from 'lucide-react'
import CreateMcpConnectionModal from './Mcp/CreateMcpConnectionModal'
import EmojiPicker from './EmojiPicker'
import { useRegistry, useRegistrySyncStatus, useWorkspaceSwitch, useCreateWorkspace, useIdentity, useEngineManager } from '../hooks/useWorkspace'
import { useToast } from './Toast'
import { timeAgo } from '../utils/writerUtils'
import { parseWorkspaceFromHash } from '../engine/parseUrl'
import type { WorkspaceEntry } from '../engine/types'
import type { SyncState } from '../engine/types'
import './WorkspacePicker.css'

interface WorkspaceUiItem extends WorkspaceEntry {

    order: number
    lastOpenedAt: number
    color?: string
    suspended: boolean
    syncState: SyncState | undefined
}

interface WorkspacePickerProps {
    isOpen: boolean
    onClose: () => void
    onOpenProfile?: () => void
}

export interface ParsedWorkspaceShareLink {
    kind: 'legacy-capability' | 'invite'
    blobId: string
    capability: string
    canWrite: boolean
    title: string
    emoji: string
    apiBaseUrl: string
    cleanUrl: string
    hash: string
}

function normalizeWorkspaceShareLinkInput(input: string): URL {
    const trimmed = input.trim()
    if (!trimmed) throw new Error('Invalid link')

    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://secureblob.invalid'
    if (trimmed.startsWith('sb-invite:')) {
        return new URL(`/#invite=${trimmed.slice('sb-invite:'.length)}`, currentOrigin)
    }
    if (trimmed.startsWith('invite=')) {
        return new URL(`/#${trimmed}`, currentOrigin)
    }

    return new URL(trimmed, currentOrigin)
}

export function parseWorkspaceShareLink(url: string): ParsedWorkspaceShareLink {
    const parsedUrl = normalizeWorkspaceShareLinkInput(url)
    const hash = parsedUrl.hash.startsWith('#') ? parsedUrl.hash.slice(1) : parsedUrl.hash
    const cleanUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`

    if (hash.startsWith('invite=')) {
        const parsed = parseWorkspaceFromHash(parsedUrl.hash)
        if (!parsed.entry || !parsed.inviteV2) throw new Error('Invalid link')

        return {
            kind: 'invite',
            blobId: parsed.entry.blobId,
            capability: parsed.entry.capability,
            canWrite: parsed.entry.canWrite ?? false,
            title: parsed.entry.label,
            emoji: parsed.entry.emoji,
            apiBaseUrl: parsed.entry.apiBaseUrl,
            cleanUrl,
            hash: parsedUrl.hash,
        }
    }

    const [blobId, ...rest] = hash.split('&')
    const params = new URLSearchParams(rest.join('&'))
    const capability = params.get('k') || ''

    if (!blobId || !capability) throw new Error('Invalid link')

    return {
        kind: 'legacy-capability',
        blobId,
        capability,
        canWrite: params.has('w'),
        title: params.get('title') || 'Shared',
        emoji: params.get('emoji') || '🔗',
        apiBaseUrl: parsedUrl.origin.replace(/\/$/, '') + '/api',
        cleanUrl,
        hash: parsedUrl.hash,
    }
}

export default function WorkspacePicker({ isOpen, onClose, onOpenProfile }: WorkspacePickerProps) {
    const { entries, updateEntry, addEntry, removeEntry } = useRegistry()
    const { activeBlobId, suspendedBlobIds, syncStates, activateWorkspace } = useWorkspaceSwitch()
    const { hasIdentity } = useIdentity()
    const createWorkspace = useCreateWorkspace()
    const registrySyncStatus = useRegistrySyncStatus()
    const mgr = useEngineManager()
    const toast = useToast()
    const [query, setQuery] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)

    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, ws: WorkspaceUiItem } | null>(null)
    const [isMcpOpen, setIsMcpOpen] = useState(false)
    const [pendingLeave, setPendingLeave] = useState<WorkspaceUiItem | null>(null)
    const [pendingLink, setPendingLink] = useState(false)
    const [linkInput, setLinkInput] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const closeMenu = () => setContextMenu(null)
        window.addEventListener('click', closeMenu)
        return () => window.removeEventListener('click', closeMenu)
    }, [])

    // Escape to dismiss leave modal
    useEffect(() => {
        if (!pendingLeave) return
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); setPendingLeave(null) }
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [pendingLeave])

    // Escape to dismiss link modal
    useEffect(() => {
        if (!pendingLink) return
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); setPendingLink(false); setLinkInput('') }
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [pendingLink])

    const handleContextMenu = (e: React.MouseEvent, ws: WorkspaceUiItem) => {
        e.preventDefault()
        const menuWidth = 220
        const menuHeight = ws.blobId !== activeBlobId ? 100 : 56
        const margin = 8
        const x = Math.max(margin, Math.min(e.clientX, window.innerWidth - menuWidth - margin))
        const y = Math.max(margin, Math.min(e.clientY, window.innerHeight - menuHeight - margin))
        setContextMenu({ x, y, ws })
    }

    // Map registry entries to workspace items with UI metadata
    const workspaces = useMemo<WorkspaceUiItem[]>(() => entries.map((entry, i) => ({
        ...entry,

        order: i,
        lastOpenedAt: Date.now() - i * 3600000,
        color: i === 0 ? '#6366f1' : i === 1 ? '#0891b2' : undefined,
        suspended: suspendedBlobIds.includes(entry.blobId),
        syncState: syncStates[entry.blobId],
    })), [entries, suspendedBlobIds, syncStates])

    // Focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setQuery('')
            setSelectedIndex(0)
            // Small delay to let animation start
            setTimeout(() => inputRef.current?.focus(), 50)
        }
    }, [isOpen])

    // Filter and group workspaces
    const { recent, all } = useMemo(() => {
        const q = query.toLowerCase().trim()
        const filtered = q
            ? workspaces.filter(w => w.label.toLowerCase().includes(q) || w.emoji.includes(q))
            : workspaces

        const sorted = [...filtered].sort((a, b) => (b.lastOpenedAt || 0) - (a.lastOpenedAt || 0))
        const recent = q ? [] : sorted.slice(0, 8)
        const all = q ? sorted : []

        return { recent, all }
    }, [query, workspaces])

    const flatList = [...recent, ...all]

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen || pendingLeave || pendingLink) return

        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
            } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedIndex(i => Math.min(i + 1, flatList.length - 1))
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedIndex(i => Math.max(i - 1, 0))
            } else if (e.key === 'Enter' && flatList[selectedIndex]) {
                e.preventDefault()
                void handleSwitch(flatList[selectedIndex])
            }
        }

        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [isOpen, flatList, pendingLeave, pendingLink, selectedIndex, onClose])

    // Scroll selected into view
    useEffect(() => {
        if (!listRef.current) return
        const selected = listRef.current.querySelector('.ws-item.selected')
        if (selected) selected.scrollIntoView({ block: 'nearest' })
    }, [selectedIndex])

    async function handleSwitch(ws: WorkspaceUiItem) {
        await activateWorkspace(ws.blobId)
        onClose()
    }

    function handleOpenLink() {
        const url = linkInput.trim()
        if (!url) return
        try {
            const parsed = parseWorkspaceShareLink(url)

            // Reject tombstoned workspaces early — addEntry would silently skip them
            // and activateWorkspace would then throw "Workspace not found" with no UI feedback.
            if (parsed.kind !== 'invite' && mgr.isTombstoned(parsed.blobId)) {
                toast('This workspace was previously deleted and cannot be reopened from here.', { type: 'error' })
                return
            }

            if (parsed.kind === 'invite') {
                setPendingLink(false)
                setLinkInput('')
                onClose()
                window.location.hash = parsed.hash
                return
            }

            addEntry({
                blobId: parsed.blobId,
                label: parsed.title,
                emoji: parsed.emoji,
                apiBaseUrl: parsed.apiBaseUrl,
                capability: parsed.capability,
                canWrite: parsed.canWrite,
            })
            activateWorkspace(parsed.blobId).then(() => {
                window.history.replaceState(null, '', parsed.cleanUrl)
                setPendingLink(false)
                setLinkInput('')
                onClose()
            }).catch(err => {
                toast(`Could not open workspace: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
                setPendingLink(false)
                setLinkInput('')
            })
        } catch (err) {
            toast((err as Error).message === 'Invalid link' ? 'Invalid link' : 'Could not parse link', { type: 'error' })
        }
    }

    if (!isOpen) return null

    return (
        <>
            <div className="ws-picker-backdrop" onClick={onClose} />
            <div className="ws-picker">
                <div className="ws-picker-search">
                    <Search size={16} className="ws-picker-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setSelectedIndex(0) }}
                        placeholder="Search workspace…"
                        className="ws-picker-input"
                    />
                </div>

                <div className="ws-picker-list" ref={listRef}>

                    {registrySyncStatus.state === 'error' && registrySyncStatus.isInitial && (
                        <div className="ws-picker-sync-error">
                            <span>⚠️ Kunde inte synka dina workspaces</span>
                            <button onClick={() => void mgr.syncAllNow()}>Försök igen</button>
                        </div>
                    )}
                    {recent.length > 0 && (
                        <div className="ws-picker-section">
                            <div className="ws-picker-section-label">Recent</div>
                            {recent.map((ws, i) => (
                                <WorkspaceItemRow
                                    key={ws.blobId}
                                    ws={ws}
                                    isActive={ws.blobId === activeBlobId}
                                    isSelected={i === selectedIndex}
                                    onClick={() => { void handleSwitch(ws) }}
                                    onContextMenu={(e) => handleContextMenu(e, ws)}
                                    onUpdate={(changes) => updateEntry(ws.blobId, changes)}
                                    showTime
                                />
                            ))}
                        </div>
                    )}

                    {all.length > 0 && (
                        <div className="ws-picker-section">
                            <div className="ws-picker-section-label">Alla</div>
                            {all.map((ws, i) => (
                                <WorkspaceItemRow
                                    key={ws.blobId}
                                    ws={ws}
                                    isActive={ws.blobId === activeBlobId}
                                    isSelected={i + recent.length === selectedIndex}
                                    onClick={() => { void handleSwitch(ws) }}
                                    onContextMenu={(e) => handleContextMenu(e, ws)}
                                    onUpdate={(changes) => updateEntry(ws.blobId, changes)}
                                />
                            ))}
                        </div>
                    )}

                    {flatList.length === 0 && query && (
                        <div className="ws-picker-empty">Inga workspace matchar &quot;{query}&quot;</div>
                    )}
                </div>

                <div className="ws-picker-actions">
                    {createWorkspace && (
                        <button className="ws-picker-action" onClick={async () => { await createWorkspace(); onClose() }}>
                            <Plus size={16} /> New workspace
                        </button>
                    )}
                    {hasIdentity && (
                        <button className="ws-picker-action" onClick={() => { setPendingLink(true); setLinkInput('') }}>
                            <Link2 size={16} /> Open from link
                        </button>
                    )}
                    <button className="ws-picker-action" onClick={() => { onClose(); onOpenProfile?.() }}>
                        <Settings size={16} /> Profile Settings
                    </button>
                </div>

                <div className="ws-picker-footer">
                    <kbd>↑↓</kbd> navigate <kbd>↵</kbd> select <kbd>esc</kbd> close
                </div>

                {contextMenu && (
                    <div
                        className="ws-context-menu"
                        style={{
                            position: 'fixed',
                            top: contextMenu.y,
                            left: contextMenu.x,
                            zIndex: 2000,
                            background: '#1e1e1e',
                            border: '1px solid #333',
                            borderRadius: '6px',
                            padding: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >

                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                padding: '6px 12px',
                                width: '100%',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '13px',
                                borderRadius: '4px'
                            }}
                            className="ws-context-item"
                            onMouseEnter={e => e.currentTarget.style.background = '#333'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            onClick={() => { setIsMcpOpen(true); setContextMenu(null) }}
                        >
                            <Cable size={14} /> Add to MCP...
                        </button>
                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                padding: '6px 12px',
                                width: '100%',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '13px',
                                borderRadius: '4px'
                            }}
                            className="ws-context-item"
                            onMouseEnter={e => e.currentTarget.style.background = '#333'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            onClick={() => {
                                window.dispatchEvent(new CustomEvent('sb:open-workspace-settings', {
                                    detail: { blobId: contextMenu.ws.blobId },
                                }))
                                setContextMenu(null)
                                onClose()
                            }}
                        >
                            <Settings size={14} /> Workspace settings
                        </button>
                        {contextMenu.ws.blobId !== activeBlobId && (
                            <button
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#ef4444',
                                    padding: '6px 12px',
                                    width: '100%',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    borderRadius: '4px'
                                }}
                                className="ws-context-item"
                                onMouseEnter={e => e.currentTarget.style.background = '#333'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                onClick={() => {
                                    setPendingLeave(contextMenu.ws)
                                    setContextMenu(null)
                                }}
                            >
                                <LogOut size={14} /> Leave workspace
                            </button>
                        )}
                    </div>
                )}

            </div>

            <CreateMcpConnectionModal
                isOpen={isMcpOpen}
                onClose={() => setIsMcpOpen(false)}
                initialWorkspaceId={contextMenu?.ws.blobId}
            />

            {pendingLeave && (
                <>
                    <div className="ws-delete-backdrop" onClick={() => setPendingLeave(null)} />
                    <div className="ws-leave-modal" role="dialog" aria-label="Leave workspace">
                        <div className="ws-leave-body">
                            <h3><LogOut size={18} /> Leave &quot;{pendingLeave.label}&quot;?</h3>
                            <p>Removed from your list only. Others keep access.</p>
                            <p>You can rejoin via a share link.</p>
                        </div>
                        <div className="ws-leave-footer">
                            <button className="ws-leave-cancel" onClick={() => setPendingLeave(null)}>Cancel</button>
                            <button className="ws-leave-submit" onClick={() => {
                                removeEntry(pendingLeave.blobId)
                                toast(`🚪 Left ${pendingLeave.label}. Rejoin via share link.`, { type: 'info' })
                                setPendingLeave(null)
                            }}>
                                <LogOut size={14} /> Leave
                            </button>
                        </div>
                    </div>
                </>
            )}

            {pendingLink && (
                <>
                    <div className="ws-link-backdrop" onClick={() => { setPendingLink(false); setLinkInput('') }} />
                    <div className="ws-link-modal" role="dialog" aria-label="Open from link">
                        <div className="ws-link-body">
                            <h3><Link2 size={18} /> Open from link</h3>
                            <p>Paste a share link to open or rejoin a workspace.</p>
                            <input
                                className="ws-link-input"
                                type="text"
                                value={linkInput}
                                onChange={e => setLinkInput(e.target.value)}
                                placeholder="Paste a share link…"
                                autoFocus
                                onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); handleOpenLink() }
                                }}
                            />
                        </div>
                        <div className="ws-link-footer">
                            <button className="ws-link-cancel" onClick={() => { setPendingLink(false); setLinkInput('') }}>Cancel</button>
                            <button className="ws-link-submit" onClick={handleOpenLink}>
                                <Link2 size={14} /> Open
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

interface WorkspaceItemRowProps {
    ws: WorkspaceUiItem
    isActive: boolean
    isSelected: boolean
    onClick: () => void
    onContextMenu: (e: React.MouseEvent) => void
    onUpdate: (changes: { label?: string; emoji?: string }) => void
    showTime?: boolean
}

function WorkspaceItemRow({ ws, isActive, isSelected, onClick, onContextMenu, onUpdate, showTime }: WorkspaceItemRowProps) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(ws.label)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editing])

    const commitRename = useCallback(() => {
        const trimmed = draft.trim()
        if (trimmed && trimmed !== ws.label) {
            onUpdate({ label: trimmed })
        }
        setEditing(false)
    }, [draft, ws.label, onUpdate])

    useEffect(() => { setDraft(ws.label) }, [ws.label])

    return (
        <div
            className={`ws-item ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
            onClick={editing ? undefined : onClick}
            onContextMenu={onContextMenu}
            style={ws.color ? { '--ws-accent': ws.color } as CSSProperties : undefined}
        >
            <span
                className={`ws-item-emoji ${editing ? 'ws-item-emoji-editable' : ''}`}
                onClick={(e) => { e.stopPropagation(); if (editing) { setShowEmojiPicker(v => !v) } else { setEditing(true); setShowEmojiPicker(true) } }}
                role="button"
                title="Change icon"
                style={{ position: 'relative', cursor: 'pointer' }}
            >
                {ws.emoji}
                {showEmojiPicker && (
                    <EmojiPicker
                        currentEmoji={ws.emoji}
                        onSelect={(emoji) => onUpdate({ emoji })}
                        onClose={() => setShowEmojiPicker(false)}
                    />
                )}
            </span>
            {editing ? (
                <input
                    ref={inputRef}
                    className="ws-item-rename-input"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                        if (e.key === 'Escape') { setEditing(false); setDraft(ws.label) }
                    }}
                    onClick={e => e.stopPropagation()}
                />
            ) : (
                <div className="ws-item-info">
                    <span className="ws-item-label">{ws.label}</span>
                    <span className="ws-item-hash">{ws.blobId.slice(0, 8)}</span>
                </div>
            )}
            {!editing && (
                <button
                    className="ws-item-edit-btn"
                    title="Edit workspace"
                    onClick={e => { e.stopPropagation(); setEditing(true) }}
                    aria-label="Edit workspace name and icon"
                >
                    <Pencil size={13} />
                </button>
            )}
            {showTime && ws.lastOpenedAt && (
                <span className="ws-item-time">{timeAgo(ws.lastOpenedAt, { maxDepth: 'days' })}</span>
            )}
            {ws.suspended && !isActive && <span className="ws-item-time">suspended</span>}
            {ws.syncState?.syncing && <span className="ws-item-time">syncing</span>}

            {isActive && <Check size={14} className="ws-item-check" />}
        </div>
    )
}
